import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';

import { debounce } from 'lodash';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

import Boards from './Boards';
import PageWrapper from '../../common/PageWrapper';

import { Board } from '@/models/BoardModel';
import { Room } from '@/models/RoomModel';
import { Community } from '@/models/CommunityModel';

import { selectIsLoadingBoards, selectBoards, selectIsRequested } from '../../ducks/Boards';
import {
  actions as communityActions,
  selectCommunities,
  selectActiveCommunity,
  selectIsLoadingCommunities,
} from '../../ducks/Communities';
import {
  actions as roomActions,
  selectRooms,
  selectActiveRoom,
  selectIsLoadingRooms,
  selectRoomsRequested,
} from '../../ducks/Rooms';
import * as boardsActions from '../../thunks/Boards';
import * as roomsActions from '../../thunks/Rooms';
import { renderSelectField } from '../../utils/RenderUtil';

const useStyles = () => ({
  field: {
    minWidth: '200px',
  },
  loading: {
    marginTop: '15px',
  },
});

const mapStateToProps = () =>
  createStructuredSelector({
    communityId: selectActiveCommunity(),
    roomId: selectActiveRoom(),
    communities: selectCommunities(),
    isLoadingCommunities: selectIsLoadingCommunities(),
    rooms: selectRooms(),
    isLoadingRooms: selectIsLoadingRooms(),
    roomsRequested: selectRoomsRequested(),
    boards: selectBoards(),
    isLoadingBoards: selectIsLoadingBoards(),
    boardsRequested: selectIsRequested(),
  });

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      ...boardsActions,
      ...roomsActions,
      setActiveCommunity: communityActions.setActiveCommunity,
      setActiveRoom: roomActions.setActiveRoom,
    },
    dispatch,
  ),
});

const BoardsContainer = ({
  communityId,
  roomId,
  communities,
  isLoadingCommunities,
  rooms,
  isLoadingRooms,
  roomsRequested,
  boards,
  isLoadingBoards,
  boardsRequested,
  actions,
}: {
  communityId: string;
  roomId: string;
  communities: Community[];
  isLoadingCommunities: boolean;
  rooms: Room[];
  isLoadingRooms: boolean;
  roomsRequested: { communityId: string };
  boards: Board[];
  isLoadingBoards: boolean;
  boardsRequested: { communityId: string; roomId: string };
  actions: any;
}) => {
  const classes = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const communityIdFromUrl = searchParams.get('communityId') || '';
  const roomIdFromUrl = searchParams.get('roomId') || '';

  const isLoading =
    isLoadingCommunities ||
    isLoadingRooms ||
    (communityIdFromUrl && !(roomsRequested.communityId == communityIdFromUrl)) ||
    isLoadingBoards ||
    (roomIdFromUrl && !(boardsRequested.communityId == communityIdFromUrl && boardsRequested.roomId == roomIdFromUrl));

  useEffect(() => {
    if (!communityIdFromUrl && communityId) {
      const params: { communityId: string; roomId?: string } = { communityId };

      if (!roomIdFromUrl && roomId) {
        params.roomId = roomId;
      }

      setSearchParams(params);
    }

    if (communityIdFromUrl && roomIdFromUrl && roomIdFromUrl !== roomId) {
      actions.setActiveRoom({ roomId: roomIdFromUrl });
    }
  }, [communityId, roomId, communityIdFromUrl, roomIdFromUrl, setSearchParams, actions]);

  useEffect(() => {
    if (communityIdFromUrl && roomsRequested.communityId !== communityIdFromUrl) {
      const fetchRooms = debounce(() => {
        actions.fetchRooms({ communityId: communityIdFromUrl });
      }, 500);

      fetchRooms();

      return () => fetchRooms.cancel();
    }
  }, [communityIdFromUrl, roomsRequested, actions]);

  useEffect(() => {
    if (
      communityIdFromUrl &&
      roomIdFromUrl &&
      (boardsRequested.communityId !== communityIdFromUrl || boardsRequested.roomId !== roomIdFromUrl)
    ) {
      const fetchBoards = debounce(() => {
        actions.fetchBoards({ communityId: communityIdFromUrl, roomId: roomIdFromUrl });
      }, 500);

      fetchBoards();

      return () => fetchBoards.cancel();
    }
  }, [communityIdFromUrl, roomIdFromUrl, boardsRequested, actions]);

  const handleCommunityChange = (event: any) => {
    const communityId = event.target.value;

    if (communityId != communityIdFromUrl) {
      setSearchParams({ communityId });
      actions.setActiveCommunity({ communityId });
      actions.setActiveRoom({ roomId: '' });
    }
  };

  const handleRoomChange = (event: any) => {
    const roomId = event.target.value;

    if (roomId != roomIdFromUrl) {
      setSearchParams({ communityId: communityIdFromUrl, roomId });
      actions.setActiveRoom({ roomId });
    }
  };

  const communityOptions = communities.map((community) => {
    let label = community.name;

    if (community.project_name) {
      label = community.name + ' - ' + community.project_name;
    }

    if (community.name === '') {
      label = community.id;
    }

    return {
      id: community.id,
      name: label,
    };
  });

  return (
    <React.Fragment>
      <PageWrapper title={intl.get('app_bar.boards')}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {renderSelectField({
            name: 'communityId',
            label: intl.get('boards.community'),
            value: communityId,
            handleChange: handleCommunityChange,
            options: communityOptions,
            className: classes.field,
          })}
          {renderSelectField({
            name: 'roomId',
            label: intl.get('boards.room'),
            value: roomId,
            handleChange: handleRoomChange,
            options: rooms,
            className: classes.field,
            disabled: isLoadingRooms || (communityIdFromUrl && communityIdFromUrl !== roomsRequested.communityId),
          })}
        </Box>
        {isLoading && <LinearProgress sx={classes.loading} />}
        {!isLoading && <Boards roomId={roomId} rooms={rooms} boards={boards} />}
      </PageWrapper>
    </React.Fragment>
  );
};

const ConnectedBoardsContainer = connect(mapStateToProps, mapDispatchToProps)(BoardsContainer);

export default ConnectedBoardsContainer;
